import { t, Trans } from '@lingui/macro'
import Column from 'components/Column'
import RoutingDiagram, { ROUTING_DIAGRAM_VIEWS, RoutingDiagramViewMode } from 'components/RoutingDiagram/RoutingDiagram'
import { RowBetween, RowFixed } from 'components/Row'
import useAutoRouterSupported from 'hooks/useAutoRouterSupported'
import { useEffect, useState } from 'react'
import { X } from 'react-feather'
import { RefreshCw } from 'react-feather'
import { InterfaceTrade } from 'state/routing/types'
import styled, { keyframes } from 'styled-components/macro'
import { Separator, ThemedText } from 'theme'
import getRoutingDiagramEntries from 'utils/getRoutingDiagramEntries'

import { RouterLabelX } from './RouterLabel'

const CloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px 8px;
  margin: -4px;
  cursor: pointer;
  border-radius: 6px;
  transition: opacity 200ms ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accentAction};
    outline-offset: 2px;
  }
`

const CloseIcon = styled(X)`
  width: 14px;
  height: 14px;

  > * {
    stroke: currentColor;
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingIcon = styled(RefreshCw)`
  width: 14px;
  height: 14px;
  animation: ${spin} 1s linear infinite;

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`

const HeaderLeftGroup = styled(RowFixed)`
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const HeaderRight = styled(RowFixed)`
  align-items: center;
  gap: 8px;
`

const RouteLoadingContainer = styled.div`
  width: 100%;
  min-height: 200px;
  border-radius: 8px;
  //border: 1px solid ${({ theme }) => theme.backgroundOutline};
  //background: ${({ theme }) => theme.backgroundModule};
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
`

const RouteLoadingAnimation = styled.div`
  width: 60px;
  height: 60px;

  svg {
    width: 100%;
    height: 100%;
  }
`

const RouteLoadingSkeleton = styled.div`
  width: 80%;
  max-width: 360px;
  height: 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.backgroundInteractive};
  opacity: 0.6;
`

const StatusRow = styled(RowFixed)`
  align-items: center;
  gap: 6px;
`

const ViewToggle = styled.div`
  display: inline-flex;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  background: ${({ theme }) => theme.backgroundSurface};
  overflow: hidden;
`

const ViewToggleOption = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.backgroundInteractive : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.textPrimary : theme.textSecondary)};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.accentAction};
  }
`

const ROUTE_VIEW_STORAGE_KEY = 'piteas:route-view-mode'

const isRoutingDiagramViewMode = (value: string | null): value is RoutingDiagramViewMode => {
  return !!value && Object.values(ROUTING_DIAGRAM_VIEWS).includes(value as RoutingDiagramViewMode)
}

export default function SwapRoute({
  trade,
  syncing,
  refreshing = syncing,
  onClose,
  showViewSelect = true,
  canClose = true,
}: {
  trade?: InterfaceTrade
  syncing: boolean
  refreshing?: boolean
  onClose?: () => void
  showViewSelect?: boolean
  canClose?: boolean
}) {
  const autoRouterSupported = useAutoRouterSupported()

  const routes = trade ? getRoutingDiagramEntries(trade) : []

  const [viewMode, setViewMode] = useState<RoutingDiagramViewMode>(ROUTING_DIAGRAM_VIEWS.CLASSIC)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const storedMode = window.localStorage.getItem(ROUTE_VIEW_STORAGE_KEY)
    if (isRoutingDiagramViewMode(storedMode)) {
      setViewMode(storedMode)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(ROUTE_VIEW_STORAGE_KEY, viewMode)
  }, [viewMode])

  return (
    <Column gap="md">
      <RowBetween>
        <HeaderLeft>
          <HeaderLeftGroup>
            <RouterLabelX />
            {showViewSelect && (
              <ViewToggle role="group" aria-label={t`Route visualization mode`}>
                <ViewToggleOption
                  type="button"
                  disabled={!trade}
                  $active={viewMode === ROUTING_DIAGRAM_VIEWS.CLASSIC}
                  onClick={() => setViewMode(ROUTING_DIAGRAM_VIEWS.CLASSIC)}
                >
                  <Trans>Classic</Trans>
                </ViewToggleOption>
                <ViewToggleOption
                  type="button"
                  disabled={!trade}
                  $active={viewMode === ROUTING_DIAGRAM_VIEWS.DETAILED}
                  onClick={() => setViewMode(ROUTING_DIAGRAM_VIEWS.DETAILED)}
                >
                  <Trans>Detailed</Trans>
                </ViewToggleOption>
              </ViewToggle>
            )}
          </HeaderLeftGroup>
          {refreshing && <LoadingIcon aria-label={t`Routes refreshing`} />}
        </HeaderLeft>
        <HeaderRight>
          {onClose && canClose && (
            <CloseButton type="button" onClick={onClose} aria-label={t`Close route details`}>
              <CloseIcon />
              <Trans>Close</Trans>
            </CloseButton>
          )}
        </HeaderRight>
      </RowBetween>
      <Separator />
      {trade && !syncing && routes.length > 0 ? (
        <RoutingDiagram
          currencyIn={trade.inputAmount.currency}
          currencyOut={trade.outputAmount.currency}
          routes={routes}
          chainId={trade.inputAmount.currency.chainId ?? trade.outputAmount.currency.chainId}
          viewMode={viewMode}
        />
      ) : (
        <RouteLoadingContainer>
          {trade ? (
            <RouteLoadingAnimation aria-label={t`Routes refreshing`}>
              <svg width="60" height="60" viewBox="0 0 50 50">
                <path
                  d="M10,25 L20,25 L20,15 L30,15 L30,35 L40,35"
                  fill="none"
                  stroke="#d4125b"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <animate attributeName="stroke-dasharray" values="0,100;100,0" dur="2s" repeatCount="indefinite" />
                </path>
                <circle cx="20" cy="25" r="2" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="0s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="30" cy="15" r="2" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="0.5s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="40" cy="25" r="2" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </RouteLoadingAnimation>
          ) : (
            <RouteLoadingAnimation aria-label={t`Waiting for quote`}>
              <svg width="60" height="60" viewBox="0 0 50 50">
                <circle cx="20" cy="25" r="4" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="0s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="30" cy="25" r="4" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="0.4s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="40" cy="25" r="4" fill="#d4125b">
                  <animate
                    attributeName="fill-opacity"
                    values="1;0.3;1"
                    dur="1.5s"
                    begin="0.8s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </RouteLoadingAnimation>
          )}
          <ThemedText.BodySmall color="textTertiary">
            {trade ? (
              <Trans>Finding the best route for your trade ...</Trans>
            ) : (
              <Trans>Waiting for route quote ...</Trans>
            )}
          </ThemedText.BodySmall>
          <RouteLoadingSkeleton />
        </RouteLoadingContainer>
      )}
      {autoRouterSupported && (
        <>
          <Separator />
          <StatusRow>
            <ThemedText.Caption color="textTertiary">
              <Trans>Routes refresh automatically while pathfinder watches each block for the best offer.</Trans>
            </ThemedText.Caption>
          </StatusRow>
        </>
      )}
    </Column>
  )
}
